// Resolve the realtime WebSocket endpoint.
// 1. An explicit REACT_APP_WS_URL always wins — set it when the WS host differs
//    from the API host.
// 2. Otherwise derive it from REACT_APP_API_URL, so the socket automatically
//    follows whichever environment the API points at: dev
//    (http://localhost:5001/api → ws://localhost:5001/ws) and prod
//    (https://console.compliancesarathi.in/api → wss://console.compliancesarathi.in/ws)
//    share a host and differ only by path (/api vs /ws).
// 3. Last resort: the page's own origin. We never hard-fall back to a literal
//    "localhost", which silently breaks realtime notifications in production.
const resolveWsUrl = () => {
  if (process.env.REACT_APP_WS_URL) return process.env.REACT_APP_WS_URL;
  try {
    const api = new URL(process.env.REACT_APP_API_URL);
    return `${api.protocol === 'https:' ? 'wss:' : 'ws:'}//${api.host}/ws`;
  } catch {
    return `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`;
  }
};

// Dev-only chatter — silent in production so the prod console isn't spammed.
const wsLog = (...args) => {
  if (process.env.NODE_ENV !== 'production') console.log(...args);
};

class WebSocketService {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectInterval = 5000;
    this.heartbeatInterval = null;
    this.reconnectTimer = null;
    this.isConnecting = false;
    this.listeners = new Map();
    this.url = resolveWsUrl();
  }

  async connect(getTicket) {
    // Remember the ticket provider so reconnects can fetch a fresh single-use ticket.
    if (typeof getTicket === 'function') this.getTicket = getTicket;

    wsLog('🔌 Frontend WebSocket connect called:', {
      hasProvider: !!this.getTicket, 
      currentState: this.ws ? this.ws.readyState : 'no connection',
      isConnecting: this.isConnecting 
    });
    
    // Check if we already have a valid connection
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      wsLog('🔌 Frontend WebSocket already connected');
      return;
    }
    
    if (this.isConnecting) {
      wsLog('🔌 Frontend WebSocket already connecting');
      return;
    }

    // Clean up any existing but invalid connection
    if (this.ws && this.ws.readyState !== WebSocket.OPEN) {
      this.ws.close();
      this.ws = null;
    }

    this.isConnecting = true;
    
    try {
      // Fetch a fresh single-use ticket — the JWT no longer rides in the URL.
      const ticket = this.getTicket ? await this.getTicket() : null;
      if (!ticket) { this.isConnecting = false; return; }
      wsLog('🔌 Frontend WebSocket connecting');
      this.ws = new WebSocket(`${this.url}?ticket=${ticket}`);
      
      this.ws.onopen = () => {
        wsLog('🔌 Frontend WebSocket connected');
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.startHeartbeat();
        this.emit('connected');
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) {
          console.error('🔌 Frontend WebSocket error parsing message:', error);
        }
      };

      this.ws.onclose = (event) => {
        wsLog('🔌 Frontend WebSocket disconnected:', { code: event.code, reason: event.reason });
        this.isConnecting = false;
        this.stopHeartbeat();
        this.emit('disconnected');
        
        // Attempt to reconnect if not a normal closure
        if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = (error) => {
        // Dev-only: the error Event carries no useful detail (the close code
        // does), so logging it in prod just floods the console when /ws isn't
        // reachable. Listeners still get the 'error' event to react to.
        wsLog('🔌 Frontend WebSocket error:', error);
        this.isConnecting = false;
        this.emit('error', error);
      };

    } catch (error) {
      this.isConnecting = false;
      this.emit('error', error);
    }
  }

  disconnect() {
    // Cancel any pending reconnect so a timer scheduled before logout can't fire
    // afterwards and resurrect a socket the app no longer owns (zombie socket).
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempts = 0;
    this.isConnecting = false;
    this.stopHeartbeat();

    if (this.ws) {
      this.ws.close(1000, 'Client disconnect'); // code 1000 → onclose won't reconnect
      this.ws = null;
    }
  }

  scheduleReconnect() {
    this.reconnectAttempts++;

    // Track the timer so disconnect() can cancel a pending reconnect.
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      // Reconnect only if still authenticated; connect() reuses the stored ticket provider.
      const stillAuthed = localStorage.getItem('cs_token') || sessionStorage.getItem('cs_token');
      if (stillAuthed) {
        this.connect();
      }
    }, this.reconnectInterval);
  }

  startHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000); // Send ping every 30 seconds
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  handleMessage(data) {
    wsLog('🔌 Frontend WebSocket received message:', data);
    const { type, payload } = data;
    
    switch (type) {
      case 'pong':
        // Heartbeat response
        break;
      case 'notification':
        wsLog('🔌 Frontend received notification:', payload);
        this.emit('notification', payload);
        break;
      case 'assignment_update':
        wsLog('🔌 Frontend received assignment update:', payload);
        this.emit('assignment_update', payload);
        break;
      case 'system_message':
        wsLog('🔌 Frontend received system message:', payload);
        this.emit('system_message', payload);
        break;
      default:
        wsLog('🔌 Frontend received unknown message type:', type);
    }
  }

  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  // Event listener methods
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('Error in WebSocket event callback:', error);
        }
      });
    }
  }

  getConnectionState() {
    if (!this.ws) return 'disconnected';
    
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING: return 'connecting';
      case WebSocket.OPEN: return 'connected';
      case WebSocket.CLOSING: return 'closing';
      case WebSocket.CLOSED: return 'disconnected';
      default: return 'unknown';
    }
  }

  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }
}

// Create singleton instance
const websocketService = new WebSocketService();

export default websocketService;
